// v0.3.0: Milestone 2 — app bootstrap, input, chat, sidebar, Meet-tile fallback.
// v0.4.0: Milestone 3 — mic/cam/screen controls, push-to-talk, video strip, autoplay gate.
// v0.5.0: Milestone 4 — zone toasts.
// v0.6.0: Milestone 5 — knock button.
// v0.7.0: zoom, emote hotkeys 1-5, SVG media icons, hair colour, hardware-off mic/cam.
// v0.8.0: side-view world — ladders/gravity movement, sit (X), personal desks (C), avatar editor.
// v1.0.0: Gather-style top-down world — 4-direction walking, chairs with a facing, poses on 7/8.
import { canPlace, findPath, isStandable, loadMap, placementsAt, setPlacements, tryStep, zoneAt, type OfficeMap, type PathStep, type TiledMap } from '../src/map';
import { CLOTH_COLORS, DEFAULT_AVATAR, EXPRS, FUR_COLORS, HAT_STYLES, MEDIA_CONNECT_DIST, MOVE_MIN_INTERVAL_MS, SHIRT_STYLES, TILE, type AvatarConfig, type DeskMap, type Dir, type EmoteKind, type Expr, type PlayerState, type Pose, type RoomNames, type ServerMsg, type Facing, type PlaceKind, type UserStatus, PLACE_KINDS, STYLE_COUNT } from '../src/protocol';
import { Net } from './net';
import { WorkspaceSettings } from './workspace';
import { Renderer, drawAvatarPreview, drawAvatarPartThumb, drawFurnitureThumb, furnitureHasArt, loadArt, KIND_LABEL, type RenderPlayer, type ThumbKind } from './render';
import { RtcClient } from './rtc';
import { ChatApp } from './chat';

// Material-style icon paths (24x24 viewBox).
const ICONS = {
  micOn: 'M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z',
  micOff: 'M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z',
  camOn: 'M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4z',
  camOff: 'M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z',
  screen: 'M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6zm8 1l-4 4h3v4h2v-4h3l-4-4z',
  screenStop: 'M21.22 18.02l2 2H24v-2h-2.78zm.77-2l.01-10c0-1.11-.9-2-2-2H7.22l5.23 5.23c.18-.04.36-.07.55-.07.79 0 1.5.32 2.03.84.52.52.84 1.23.84 2.03 0 .19-.03.37-.07.55l2.87 2.87c.12-.1.2-.24.3-.4H22zM0 3.06l3.11 3.11-.13.09C2.61 6.2 2.32 6.5 2.13 6.86c-.09.2-.13.42-.13.64V16c0 1.1.9 2 2 2H0v2h18.13l2.81 2.81 1.27-1.27L1.27 1.79 0 3.06zm1.99 12.98V6.98L11.4 16.4l-9.41-.36z',
};
function setButton(btn: HTMLElement, icon: string, label: string): void {
  btn.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="${icon}"/></svg>`;
  btn.title = label; btn.setAttribute('aria-label', label);
}
const EMOTE_KEYS: Record<string, EmoteKind> = { '1': 'wave', '2': 'dance', '3': 'laugh', '4': 'heart', '5': 'shock', '6': 'angry' };

interface Me { id: string; name: string; avatar: AvatarConfig; isAdmin: boolean; email: string }
interface Features { rtc: boolean; slack: boolean }
interface DirectoryUser { id: string; name: string; avatar: AvatarConfig; lastSeen: number | null; status?: UserStatus }

const $ = <T extends HTMLElement>(sel: string): T => document.querySelector(sel) as T;
const show = (el: HTMLElement, on: boolean) => { el.classList.toggle('hidden', !on); };

function toast(text: string, ms = 3500): void {
  const el = $('#toast');
  el.textContent = text;
  show(el, true);
  window.clearTimeout((el as HTMLElement & { _t?: number })._t);
  (el as HTMLElement & { _t?: number })._t = window.setTimeout(() => show(el, false), ms);
}

async function boot(): Promise<void> {
  const artReady = loadArt(); // v1.8.0: the creator's tiles and preview come from the atlas, so fetch it alongside the session
  const res = await fetch('/api/me');
  if (res.status === 401) {
    const invite = new URLSearchParams(location.search).get('invite');
    const link = $('#login-link') as HTMLAnchorElement;
    link.href = invite ? `/auth/login?invite=${encodeURIComponent(invite)}` : '/auth/login';
    show($('#invite-note'), Boolean(invite));
    show($('#login'), true);
    return;
  }
  if (!res.ok) { toast('Server error. Try reloading.'); return; }
  const data = (await res.json()) as { user: Me; features: Features; version: string };
  history.replaceState(null, '', '/');
  await artReady;
  showEnter(data.user, data.features);
}

const EXPR_EMOJI: Record<Expr, string> = { normal: '🙂', happy: '😊', laugh: '😂', heart: '😍', shock: '😲', angry: '😠', wink: '😉', cool: '😎' };

/**
 * v0.10.0: avatar creator. Builds the option rows into #<prefix>-editor, draws a live
 * preview into #<prefix>-preview, and returns a reader for the current config.
 */
function bindAvatarEditor(prefix: string, me: Me): () => AvatarConfig {
  const host = $(`#${prefix}-editor`);
  const nameEl = $(`#${prefix}-name`) as HTMLInputElement;
  nameEl.value = me.name;
  const state: AvatarConfig = {
    initials: me.avatar.initials,
    color: me.avatar.color || DEFAULT_AVATAR.color, pants: me.avatar.pants ?? DEFAULT_AVATAR.pants,
    expr: me.avatar.expr ?? DEFAULT_AVATAR.expr,
    shirtStyle: me.avatar.shirtStyle ?? DEFAULT_AVATAR.shirtStyle, pantsStyle: me.avatar.pantsStyle ?? DEFAULT_AVATAR.pantsStyle,
    fur: me.avatar.fur ?? DEFAULT_AVATAR.fur, eyes: me.avatar.eyes ?? DEFAULT_AVATAR.eyes,
    hat: me.avatar.hat ?? DEFAULT_AVATAR.hat, hatColor: me.avatar.hatColor ?? DEFAULT_AVATAR.hatColor,
  };
  const preview = $(`#${prefix}-preview`) as HTMLCanvasElement;
  // v1.8.0: option tiles are the real sprite-sheet pieces, tinted with the current colours — redrawn on every change
  // v1.8.7: every tile is the otter wearing that one option, redrawn with the current look on every change
  const thumbs: { canvas: HTMLCanvasElement; kind: ThumbKind; key: string }[] = [];
  const refresh = () => {
    drawAvatarPreview(preview, state, me.id);
    for (const t of thumbs) drawAvatarPartThumb(t.canvas, t.kind, t.key, state);
    host.querySelectorAll<HTMLElement>('[data-k]').forEach((b) => b.classList.toggle('sel', String(state[b.dataset.k as keyof AvatarConfig]) === b.dataset.v));
  };

  host.innerHTML = '';
  // v1.9.0: one section at a time — a segmented tab row (Mood · Fur · Headwear · Top) over a single panel of
  // large, uniformly framed otter tiles. Four stacked rows of small tiles read as clutter.
  const seg = document.createElement('div'); seg.className = 'seg'; host.appendChild(seg);
  const panels: HTMLElement[] = [];
  const tab = (label: string, rows: { title?: string; buttons: HTMLElement[] }[]) => {
    const i = panels.length;
    const b = document.createElement('button'); b.type = 'button'; b.className = 'seg-btn' + (i === 0 ? ' on' : ''); b.textContent = label;
    b.onclick = () => { seg.querySelectorAll('.seg-btn').forEach((x, j) => x.classList.toggle('on', j === i)); panels.forEach((q, j) => q.classList.toggle('hidden', j !== i)); };
    seg.appendChild(b);
    const q = document.createElement('div'); q.className = 'opt-panel' + (i === 0 ? '' : ' hidden');
    for (const r of rows) {
      if (r.title) { const t = document.createElement('span'); t.className = 'opt-label'; t.textContent = r.title; q.appendChild(t); }
      const wrap = document.createElement('div'); wrap.className = 'opts'; wrap.append(...r.buttons); q.appendChild(wrap);
    }
    panels.push(q); host.appendChild(q);
  };
  const choice = (k: keyof AvatarConfig, v: string | number, cls: string) => {
    const b = document.createElement('button'); b.type = 'button'; b.className = cls; b.dataset.k = k; b.dataset.v = String(v);
    b.onclick = () => { (state as unknown as Record<string, unknown>)[k] = v; refresh(); };
    return b;
  };
  const swatch = (k: keyof AvatarConfig, hex: string) => { const b = choice(k, hex, 'swatch'); b.style.background = hex; b.title = hex; return b; };
  /** a tile of the otter wearing that one option (falls back to the option's name when the atlas isn't loaded) */
  const art = (k: keyof AvatarConfig, v: string | number, kind: ThumbKind, key: string, label: string) => {
    const b = choice(k, v, kind === 'top' ? 'art-opt tall' : 'art-opt'); b.title = label;
    const c = document.createElement('canvas'); c.width = 192; c.height = kind === 'top' ? 256 : 192; b.appendChild(c); // 2x for crisp tiles
    if (drawAvatarPartThumb(c, kind, key, state)) thumbs.push({ canvas: c, kind, key });
    else { c.remove(); b.textContent = label; b.className = 'opt'; }
    return b;
  };

  tab('Mood', [{ buttons: EXPRS.map((e) => art('expr', e, 'face', e, `${EXPR_EMOJI[e]} ${e}`)) }]);
  tab('Fur', [{ buttons: FUR_COLORS.map((c) => art('fur', c, 'fur', c, c)) }]);
  tab('Headwear', [{ buttons: HAT_STYLES.map((h) => art('hat', h, 'hat', h, h)) }, { title: 'Colour', buttons: CLOTH_COLORS.map((c) => swatch('hatColor', c)) }]);
  tab('Top', [{ buttons: SHIRT_STYLES.map((s, i) => art('shirtStyle', i, 'top', String(i), s)) }, { title: 'Colour', buttons: CLOTH_COLORS.map((c) => swatch('color', c)) }]);
  const initials = $(`#${prefix}-initials`) as HTMLInputElement; initials.value = state.initials;
  initials.oninput = () => { state.initials = (initials.value.trim() || me.avatar.initials).slice(0, 2).toUpperCase(); };
  refresh();
  return () => ({ ...state, initials: (initials.value.trim() || me.avatar.initials).slice(0, 2).toUpperCase() });
}

function showEnter(me: Me, features: Features): void {
  const nameEl = $('#enter-name') as HTMLInputElement;
  const read = bindAvatarEditor('enter', me);
  $('#enter-email').textContent = me.email;
  show($('#enter-rtc-note'), !features.rtc);
  show($('#enter'), true);
  ($('#enter-form') as HTMLFormElement).onsubmit = async (ev) => {
    ev.preventDefault();
    const name = nameEl.value.trim() || me.name;
    const avatar = read();
    await fetch('/api/me', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, avatar }) });
    me.name = name; me.avatar = avatar;
    show($('#enter'), false);
    // This click is the user gesture that unlocks audio autoplay + device prompts.
    await startApp(me, features);
  };
}

async function startApp(me: Me, features: Features): Promise<void> {
  const map = loadMap((await (await fetch('/maps/hq.json')).json()) as TiledMap);
  await loadArt(); // v1.5.0: drawn sprites/tiles when public/art/atlas.* exists
  show($('#app'), true);
  const app = new App(me, features, map);
  await app.start();
}

class App {
  private net = new Net();
  private renderer: Renderer;
  private players = new Map<string, RenderPlayer>();
  private meP: RenderPlayer | null = null;
  private held = new Set<Dir>();
  private lastMoveAt = 0;
  /** last few tiles we predicted, so a server echo that lags behind doesn't yank us back */
  /** v0.19.1: steps we've predicted but the server hasn't acknowledged yet (by seq) */
  private pending: { seq: number; dir: Dir; jump: boolean }[] = [];
  private seqNo = 0;
  private pendingSince = 0;
  private resyncs = 0;
  private serverPos: { x: number; y: number } | null = null;
  private lastSentDir: Dir | null = null;
  /** Space pressed on a ladder with no direction yet — hop off if a direction follows quickly */
  /** v0.16.0: held pose (↑ = hands up, ↓ = flat) and click-to-walk route */
  private pose: Pose = 'none';
  private autoPath: PathStep[] = [];
  private autoTarget: { x: number; y: number } | null = null;
  private autoSit = false;
  private rtc: RtcClient | null = null;
  private linked = new Set<string>();
  private nearbyHintAt = 0;
  /** v1.21.0: an open "come over" ask from someone, and its auto-decline timer */
  private summonFrom: { id: string; timer: number } | null = null;
  private audioEls = new Map<string, HTMLAudioElement>();
  private videoTiles = new Map<string, { el: HTMLElement; video: HTMLVideoElement }>();
  private directory: DirectoryUser[] = [];
  /** v1.1.0: Slack-style chat */
  private chat: ChatApp | null = null;
  /** v1.3.0: everyone's status (seeded from the directory, updated live) */
  private statuses = new Map<string, UserStatus>();
  private mic = false; private cam = false; private screen = false; private ptt = false;
  private avChain: Promise<void> = Promise.resolve();
  private lastZone: string | null | undefined = undefined;
  private desks: DeskMap = {};
  /** v0.17.0: renamed meeting rooms (zone key -> display name) */
  private rooms: RoomNames = {};
  /** v0.18.0: build mode — what's selected in the palette */
  private building = false;
  /** v0.19.0: auto-sit — the tile we've been standing still on, since when, and where we last stood up */
  private stillKey: string | null = null;
  private stillSince = 0;
  private noAutoSitKey: string | null = null;
  private buildKind: PlaceKind = 'decor';
  private buildStyle = 0;
  private buildFace: Facing = 'down';
  private eraser = false;
  private canvas = $('#canvas') as HTMLCanvasElement;

  constructor(private me: Me, private features: Features, private map: OfficeMap) {
    this.renderer = new Renderer(this.canvas, map);
    this.renderer.videoFor = (id) => id === this.me.id ? ($('#self-video') as HTMLVideoElement) : this.videoTiles.get(id)?.video ?? null; // v0.19.0
    (window as unknown as { __cbo: App }).__cbo = this; // debugging handle
  }
  get debug() { return { me: this.meP && { x: this.meP.x, y: this.meP.y, sit: this.meP.sit, zone: this.meP.zone }, players: [...this.players.keys()], linked: [...this.linked], desks: this.desks, pending: this.pending.length, resyncs: this.resyncs, serverPos: this.serverPos }; }

  async start(): Promise<void> {
    this.bindUi();
    this.bindInput();
    this.bindZoom();
    this.net.onMessage = (m) => this.onMsg(m);
    this.net.onStatus = (s) => {
      const el = $('#status');
      el.textContent = s === 'open' ? 'connected' : s === 'replaced' ? 'opened in another tab' : s;
      el.dataset.state = s;
      if (s === 'open') checkVersion();
      if (s === 'replaced') toast('Ottsy was opened in another tab — this one is now idle.', 10_000);
      if (s === 'removed') { toast('Your access to this workspace was removed.', 15_000); setTimeout(() => { location.href = '/auth/logout'; }, 4000); }
    };
    // v1.21.2: a reconnect puts you back where you were standing; the network coming back / the tab waking up
    // reconnects at once; the connection's round trip shows on the status
    this.net.resumeAt = () => (this.meP ? { x: this.meP.x, y: this.meP.y } : null);
    window.addEventListener('online', () => this.net.kick());
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.net.kick(); });
    window.setInterval(() => { const el = $('#status'); if (el.dataset.state === 'open' && this.net.rtt) el.title = `Connected · ${this.net.rtt} ms round trip`; }, 5000);
    this.net.connect();
    void this.refreshDirectory();
    this.chat = new ChatApp({
      me: { id: this.me.id, name: this.me.name, avatar: this.me.avatar },
      net: this.net,
      users: () => {
        const map = new Map<string, { id: string; name: string; avatar: AvatarConfig; status?: UserStatus }>();
        for (const u of this.directory) map.set(u.id, { id: u.id, name: u.name, avatar: u.avatar, status: this.statuses.get(u.id) });
        for (const p of this.players.values()) map.set(p.id, { id: p.id, name: p.name, avatar: p.avatar, status: this.statuses.get(p.id) });
        map.set(this.me.id, { id: this.me.id, name: this.me.name, avatar: this.me.avatar, status: this.statuses.get(this.me.id) });
        return map;
      },
      isOnline: (id) => this.players.has(id),
      toast,
      sendNearby: (text) => this.net.send({ t: 'nearby', body: text }),
      nearbyCount: () => this.nearbyIds().length,
      onStatus: (id, st) => { this.statuses.set(id, st); this.renderUsers(); this.renderStatusButton(); },
      onUnread: (u, m) => {
        document.title = (u ? `(${u}) ` : '') + 'Ottsy';
        const b = $('#chat-badge'); b.textContent = String(m || u); show(b, u > 0); b.classList.toggle('mention', m > 0);
      },
    });
    void this.chat.load();
    for (const tab of document.querySelectorAll<HTMLElement>('#topnav .tab')) tab.onclick = () => this.setView(tab.dataset.view as 'office' | 'chat');
    $('#mini-open').onclick = () => this.setView('chat');
    $('#btn-status').onclick = () => this.openStatusDialog();
    const soundBox = $('#set-sound') as HTMLInputElement; soundBox.checked = localStorage.getItem('ottsy.sound') !== 'off';
    soundBox.onchange = () => localStorage.setItem('ottsy.sound', soundBox.checked ? 'on' : 'off');
    window.setInterval(() => this.renderStatusButton(), 30_000);
    window.setInterval(() => void this.refreshDirectory(), 60_000);
    if (this.features.rtc) await this.startRtc();
    else show($('#controls'), false);
    // Periodic reconcile: pulls anything the DO wants us to hear that we somehow lost.
    window.setInterval(() => void this.rtc?.resync(), 4000);
    window.addEventListener('pagehide', () => { this.net.close(); this.rtc?.stop(); });
    // Input runs on a timer, not rAF, so a throttled/background tab still moves.
    window.setInterval(() => this.tryMove(performance.now()), 25);
    requestAnimationFrame((t) => this.frame(t));
  }

  // ---------- realtime ----------

  private async startRtc(): Promise<void> {
    const rtc = new RtcClient(this.me.id);
    this.rtc = rtc;
    rtc.onRemoteTrack = (peer, name, track) => this.onRemoteTrack(peer, name, track);
    rtc.onPublishedChange = (tracks) => this.net.send({ t: 'rtc', sessionId: rtc.sessionId, tracks });
    rtc.onScreenEnded = () => { this.screen = false; this.syncAvUi(); };
    try {
      await rtc.start();
    } catch (e) {
      console.warn('[rtc] start failed', e);
      toast('Voice/video unavailable right now (SFU error). Text chat still works.');
      return;
    }
    // Nothing is published until a device is turned on; the DO still learns our session id.
    this.net.send({ t: 'rtc', sessionId: rtc.sessionId, tracks: rtc.publishedTrackNames() });
    this.syncAvUi();
  }

  private onRemoteTrack(peerId: string, name: string, track: MediaStreamTrack | null): void {
    if (name.endsWith('-mic')) {
      const old = this.audioEls.get(peerId);
      if (old) { old.srcObject = null; old.remove(); this.audioEls.delete(peerId); }
      if (track) {
        const a = document.createElement('audio');
        a.autoplay = true; a.setAttribute('playsinline', '');
        a.srcObject = new MediaStream([track]);
        $('#audio').appendChild(a);
        this.audioEls.set(peerId, a);
        a.play().catch(() => show($('#audio-banner'), true));
      }
    } else if (name.endsWith('-cam')) {
      const tile = this.ensureVideoTile(peerId);
      tile.video.srcObject = track ? new MediaStream([track]) : null;
      tile.el.classList.toggle('has-video', Boolean(track));
      if (track) tile.video.play().catch(() => { /* muted video autoplay is allowed */ });
    } else if (name.endsWith('-screen')) {
      if (track) this.showScreen(track, `${this.players.get(peerId)?.name ?? 'Someone'} is presenting`, false);
      else if (!this.screen) this.hideScreen();
    }
  }

  /** v0.15.0: Meet-style presenting — the overlay shows a share (yours or a peer's) with the filmstrip beside it. */
  private showScreen(track: MediaStreamTrack, title: string, mine: boolean): void {
    const v = $('#screen-video') as HTMLVideoElement;
    if (!(v.srcObject instanceof MediaStream) || v.srcObject.getVideoTracks()[0] !== track) v.srcObject = new MediaStream([track]);
    $('#screen-title').textContent = title;
    show($('#screen-stop'), mine);
    show($('#screen-overlay'), true);
    $('#stage').classList.add('presenting');
    v.play().catch(() => { /* ignore */ });
  }

  private hideScreen(): void {
    const v = $('#screen-video') as HTMLVideoElement;
    v.srcObject = null;
    show($('#screen-overlay'), false);
    $('#stage').classList.remove('presenting');
  }

  private ensureVideoTile(peerId: string): { el: HTMLElement; video: HTMLVideoElement } {
    let t = this.videoTiles.get(peerId);
    if (t) return t;
    const el = document.createElement('div');
    el.className = 'vtile';
    const video = document.createElement('video');
    video.autoplay = true; video.muted = true; video.setAttribute('playsinline', '');
    const label = document.createElement('span');
    label.className = 'vlabel';
    el.append(video, label);
    el.onclick = () => el.classList.toggle('big');
    $('#videos').appendChild(el);
    t = { el, video };
    this.videoTiles.set(peerId, t);
    this.updateVideoTile(peerId);
    return t;
  }

  private updateVideoTile(peerId: string): void {
    const t = this.videoTiles.get(peerId);
    if (!t) return;
    const p = this.players.get(peerId);
    const label = t.el.querySelector('.vlabel') as HTMLElement;
    label.textContent = p?.name ?? '';
    t.el.style.setProperty('--c', p?.avatar.color ?? '#555');
    (t.el.querySelector('.vlabel') as HTMLElement).dataset.initials = p?.avatar.initials ?? '';
    const visible = this.linked.has(peerId) && Boolean(p);
    show(t.el, visible);
    t.el.classList.toggle('cam-off', !(p?.cam));
    t.el.classList.toggle('mic-on', Boolean(p?.mic));
  }

  private removeVideoTile(peerId: string): void {
    const t = this.videoTiles.get(peerId);
    if (!t) return;
    t.video.srcObject = null; t.el.remove();
    this.videoTiles.delete(peerId);
  }

  /** Apply mic/cam state to hardware (serialized), then reflect the real result in the UI. */
  private syncAvUi(): void {
    this.renderAvUi();
    this.avChain = this.avChain.then(async () => {
      const wantMic = this.mic || this.ptt;
      if (this.rtc) {
        const micOk = await this.rtc.setMic(wantMic);
        if (wantMic && !micOk) { this.mic = false; this.ptt = false; toast('Microphone unavailable — check browser permissions.'); }
        const camOk = await this.rtc.setCam(this.cam);
        if (this.cam && !camOk) { this.cam = false; toast('Camera unavailable — check browser permissions.'); }
      }
      this.renderAvUi();
    }).catch((e) => console.warn('[av]', e));
  }

  private renderAvUi(): void {
    const micOn = this.mic || this.ptt;
    const mic = $('#btn-mic'), cam = $('#btn-cam'), scr = $('#btn-screen');
    mic.classList.toggle('on', micOn); cam.classList.toggle('on', this.cam); scr.classList.toggle('on', this.screen);
    setButton(mic, micOn ? ICONS.micOn : ICONS.micOff, this.ptt ? 'Talking' : micOn ? 'Mic on' : 'Mic off');
    setButton(cam, this.cam ? ICONS.camOn : ICONS.camOff, this.cam ? 'Cam on' : 'Cam off');
    setButton(scr, this.screen ? ICONS.screenStop : ICONS.screen, this.screen ? 'Stop share' : 'Share screen');
    const self = $('#self-video') as HTMLVideoElement;
    const camTrack = this.rtc?.localCamTrack();
    if (this.cam && camTrack && camTrack.readyState === 'live') {
      if (!(self.srcObject instanceof MediaStream) || self.srcObject.getVideoTracks()[0] !== camTrack) self.srcObject = new MediaStream([camTrack]);
      show($('#self-tile'), true); self.play().catch(() => { /* ignore */ });
    } else { self.srcObject = null; show($('#self-tile'), false); }
    // presenter sees their own share, like Meet
    const screenTrack = this.rtc?.localScreenTrack();
    if (this.screen && screenTrack) this.showScreen(screenTrack, "You're presenting", true);
    else if (!this.screen && $('#screen-stop').classList.contains('hidden') === false) this.hideScreen();
    this.net.send({ t: 'av', mic: micOn, cam: this.cam, screen: this.screen });
    if (this.meP) { this.meP.mic = micOn; this.meP.cam = this.cam; this.meP.screen = this.screen; }
  }

  // ---------- messages ----------

  private onMsg(m: ServerMsg): void {
    switch (m.t) {
      case 'welcome': {
        this.pending = []; this.serverPos = null; // v0.19.1: fresh session, nothing in flight
        this.players.clear();
        for (const p of m.players) this.addPlayer(p);
        this.meP = this.players.get(m.you) ?? null;
        this.desks = m.desks;
        this.rooms = m.rooms ?? {};
        setPlacements(this.map, m.placements ?? []);
        this.renderer.setRoomNames(this.rooms);
        this.lastZone = undefined;
        this.checkZone();
        this.renderUsers();
        if (this.rtc?.sessionId) this.net.send({ t: 'rtc', sessionId: this.rtc.sessionId, tracks: this.rtc.publishedTrackNames() });
        // v1.21.2: after a reconnect, tell everyone again whether my mic / camera / screen are on
        if (this.mic || this.cam || this.screen) this.net.send({ t: 'av', mic: this.mic || this.ptt, cam: this.cam, screen: this.screen });
        break;
      }
      case 'join': this.addPlayer(m.player); this.renderUsers(); this.chat?.renderSide(); break;
      case 'leave': {
        this.players.delete(m.id); this.linked.delete(m.id);
        this.removeVideoTile(m.id);
        this.renderUsers();
        break;
      }
      case 'pos': {
        const p = this.players.get(m.id);
        if (!p) break;
        if (p === this.meP) {
          // v0.19.1: seq-based reconciliation — drop everything the server has acknowledged,
          // then replay the still-pending steps on top of its position. Identical to what we
          // predicted in the normal case (no visible change); only a real disagreement moves us.
          this.serverPos = { x: m.x, y: m.y };
          // v1.7.0: a pos without a seq (sit/stand echo) acknowledges nothing — keep the predicted steps,
          // otherwise an auto-sit that lands just as you start walking yanks you back to the chair
          if (typeof m.seq === 'number') { this.pending = this.pending.filter((q) => q.seq > m.seq!); this.pendingSince = performance.now(); }
          p.sit = this.pending.length ? false : m.sit;
          let pos = { x: m.x, y: m.y };
          for (const q of this.pending) pos = tryStep(this.map, pos.x, pos.y, q.dir, q.jump) ?? pos;
          if (pos.x !== p.x || pos.y !== p.y) {
            this.resyncs++;
            p.movedAt = performance.now();
            p.x = pos.x; p.y = pos.y; p.zone = zoneAt(this.map, pos.x, pos.y)?.name ?? null;
            this.autoPath = []; this.autoTarget = null; this.autoSit = false;
          }
          if (!this.pending.length) p.zone = m.zone;
          this.checkZone();
          break;
        }
        if (p.x !== m.x || p.y !== m.y) p.movedAt = performance.now();
        p.x = m.x; p.y = m.y; p.dir = m.dir; p.sit = m.sit; p.zone = m.zone;
        p.face = m.dir;
        break;
      }
      case 'desks': this.desks = m.desks; break;
      case 'placements': setPlacements(this.map, m.placements); this.renderer.rebuildStatic(); break;
      case 'rooms': {
        const changed = Object.keys(m.rooms).find((k) => m.rooms[k] !== this.rooms[k]);
        this.rooms = m.rooms;
        this.renderer.setRoomNames(this.rooms);
        this.renderUsers();
        if (changed) toast(`Room renamed: ${m.rooms[changed]}`);
        break;
      }
      case 'pose': { const p = this.players.get(m.id); if (p) p.pose = m.pose; break; }
      case 'chat': break; // v1.1.0: legacy office chat — superseded by channels
      case 'chat-ev': this.chat?.onEvent(m.ev); break;
      case 'summon': this.onSummon(m.from, m.name); break;
      case 'summon-reply': {
        if (m.ok) toast(`${m.name} is on the way 🦦`);
        else toast(m.reason === 'busy' ? `${m.name} is on Do not disturb right now` : m.reason === 'timeout' ? `${m.name} didn't answer` : `${m.name} can't come right now`);
        break;
      }
      case 'nearby': {
        // v1.20.0: Nearby chat — into the sidebar list, and as a speech bubble over the sender
        this.chat?.onNearby(m.msg);
        const p = this.players.get(m.msg.userId);
        if (p) p.say = { text: m.msg.body, start: performance.now() };
        break;
      }
      case 'profile': {
        const p = this.players.get(m.id);
        if (p) { p.name = m.name; p.avatar = m.avatar; this.updateVideoTile(m.id); this.renderUsers(); }
        break;
      }
      case 'av': {
        const p = this.players.get(m.id);
        if (p) { p.mic = m.mic; p.cam = m.cam; p.screen = m.screen; this.updateVideoTile(m.id); }
        break;
      }
      case 'rtc': {
        const p = this.players.get(m.id);
        if (p) { p.rtc = m.rtc; p.tracks = m.tracks; }
        break;
      }
      case 'media': {
        this.linked = new Set(m.peers.map((p) => p.id));
        for (const id of this.videoTiles.keys()) this.updateVideoTile(id);
        for (const p of m.peers) if (p.tracks.some((t) => t.endsWith('-cam'))) this.ensureVideoTile(p.id);
        void this.rtc?.syncPeers(m.peers);
        break;
      }
      case 'emote': {
        const p = this.players.get(m.id);
        if (p) p.emote = { kind: m.kind, start: performance.now() };
        break;
      }
      case 'error': toast(m.msg); break;
      case 'pong': break;
    }
  }

  /** v1.20.0: who a Nearby message would reach — the server's media rule (same room, or within the connect distance) */
  private nearbyIds(): string[] {
    const me = this.meP; if (!me) return [];
    const out: string[] = [];
    for (const p of this.players.values()) {
      if (p.id === me.id) continue;
      const near = me.zone !== null || p.zone !== null ? me.zone !== null && me.zone === p.zone : Math.max(Math.abs(p.x - me.x), Math.abs(p.y - me.y)) <= MEDIA_CONNECT_DIST;
      if (near) out.push(p.id);
    }
    return out;
  }

  /** Space: a little hop on the spot. */
  private jump(): void {
    if (!this.meP || this.meP.sit) return;
    this.playEmote('jump');
  }

  private playEmote(kind: EmoteKind): void {
    if (!this.meP) return;
    if (kind === 'jump' && this.meP.emote?.kind === 'jump') return; // no double-jump
    this.meP.emote = { kind, start: performance.now() };
    this.net.send({ t: 'emote', kind });
  }

  // ---------- v0.7.0: zoom ----------

  private applyZoom(z: number): void {
    this.renderer.setZoom(z);
    try { localStorage.setItem('cbo_zoom', String(this.renderer.zoom)); } catch { /* ignore */ }
    $('#zoom-level').textContent = `${Math.round(this.renderer.zoom * 100)}%`;
  }

  private bindZoom(): void {
    let saved = 1.5;
    try { saved = parseFloat(localStorage.getItem('cbo_zoom') ?? '') || 1.5; } catch { /* ignore */ }
    this.applyZoom(saved);
    this.canvas.addEventListener('wheel', (ev) => {
      ev.preventDefault();
      this.applyZoom(this.renderer.zoom * (ev.deltaY < 0 ? 1.1 : 1 / 1.1));
    }, { passive: false });
    $('#zoom-in').onclick = () => this.applyZoom(this.renderer.zoom * 1.25);
    $('#zoom-out').onclick = () => this.applyZoom(this.renderer.zoom / 1.25);
    $('#zoom-reset').onclick = () => this.applyZoom(1.5);
  }

  private addPlayer(p: PlayerState): void {
    const existing = this.players.get(p.id);
    if (existing) { Object.assign(existing, p); return; }
    this.players.set(p.id, { ...p, pose: p.pose ?? 'none', rx: p.x, ry: p.y, face: p.dir ?? 'down' });
  }

  private checkZone(): void {
    if (!this.meP) return;
    const z = this.meP.zone;
    if (this.lastZone === undefined) { this.lastZone = z; if (z) toast(`You're in ${this.roomName(z)} — audio is private to this room`); return; }
    if (z === this.lastZone) return;
    if (z) toast(`You're in ${this.roomName(z)} — audio is private to this room`);
    else toast(`You left ${this.roomName(this.lastZone!)} — back to proximity audio`);
    this.lastZone = z;
    this.renderUsers(); // v0.17.0: keep "in <room>" under your own name current
  }

  /** v0.17.0: display name of a zone (renamed rooms keep their map key) */
  private roomName(zone: string): string { return this.rooms[zone] ?? zone; }

  /** v0.17.0: double-click a room banner → rename dialog */
  private openRename(zone: string): void {
    const dlg = $('#rename') as HTMLDialogElement;
    const input = $('#rename-input') as HTMLInputElement;
    input.value = this.roomName(zone);
    dlg.dataset.zone = zone;
    dlg.showModal();
    input.select();
  }

  // ---------- input / movement ----------

  private bindInput(): void {
    const keyDir: Record<string, Dir> = {
      ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
      w: 'up', s: 'down', a: 'left', d: 'right', W: 'up', S: 'down', A: 'left', D: 'right',
    };
    const inInput = () => {
      const a = document.activeElement;
      return a instanceof HTMLInputElement || a instanceof HTMLTextAreaElement || (a as HTMLElement | null)?.isContentEditable === true;
    };
    window.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') { (document.activeElement as HTMLElement | null)?.blur?.(); if (this.building) this.setBuilding(false); return; }
      if ((ev.key === 'k' || ev.key === 'K') && (ev.ctrlKey || ev.metaKey)) { ev.preventDefault(); this.setView('chat'); this.chat?.openSwitcher(); return; }
      if (inInput()) return;
      if ((ev.key === 'b' || ev.key === 'B') && !ev.repeat) { this.setBuilding(!this.building); return; }
      if (this.building && (ev.key === 'f' || ev.key === 'F')) { this.cycleFace(); return; }
      const d = keyDir[ev.key];
      if (d) {
        ev.preventDefault();
        this.autoPath = []; this.autoTarget = null;
        this.held.add(d);
        return;
      }
      // v1.0.0: poses are toggles — 7 = paws up, 8 = flop on the floor
      if ((ev.key === '7' || ev.key === '8') && !ev.repeat) { const want: Pose = ev.key === '7' ? 'handsup' : 'flat'; this.setPose(this.pose === want ? 'none' : want); return; }
      if (ev.key === 'Enter') { ev.preventDefault(); ($('#mini-input') as HTMLTextAreaElement).focus(); return; }
      if ((ev.key === 'k' || ev.key === 'K') && (ev.ctrlKey || ev.metaKey)) { ev.preventDefault(); this.setView('chat'); this.chat?.openSwitcher(); return; }
      if ((ev.key === 'g' || ev.key === 'G') && (ev.ctrlKey || ev.metaKey)) { ev.preventDefault(); this.setView('chat'); ($('#c-search') as HTMLInputElement).focus(); return; }
      if (ev.key === 'x' || ev.key === 'X' || (ev.key === 'Control' && !ev.repeat)) { this.interact(); return; } // v0.19.0: Ctrl = sit / stand / Meet
      if (ev.key === 'c' || ev.key === 'C') { this.deskAction(); return; }
      if (EMOTE_KEYS[ev.key]) { ev.preventDefault(); if (!ev.repeat) this.playEmote(EMOTE_KEYS[ev.key]); return; }
      if (ev.key === ' ') { ev.preventDefault(); if (!ev.repeat) this.jump(); return; }
      if ((ev.key === 'v' || ev.key === 'V') && this.rtc && !ev.repeat && !this.mic) { this.ptt = true; this.syncAvUi(); return; }
      if (ev.key === '+' || ev.key === '=') { this.applyZoom(this.renderer.zoom * 1.25); return; }
      if (ev.key === '-' || ev.key === '_') { this.applyZoom(this.renderer.zoom / 1.25); return; }
      if (ev.key === '0') { this.applyZoom(1.5); return; }
    });
    window.addEventListener('keyup', (ev) => {
      const d = keyDir[ev.key];
      if (d) { this.held.delete(d); this.lastSentDir = null; }
      if ((ev.key === 'v' || ev.key === 'V') && this.ptt) { this.ptt = false; this.syncAvUi(); }
    });
    window.addEventListener('blur', () => { this.held.clear(); if (this.ptt) { this.ptt = false; this.syncAvUi(); } });
    // v0.16.0: click anywhere in the building to walk there (ladders, lifts and drops included)
    // v0.18.0: build mode — hover shows a ghost, click places, right-click / eraser removes
    this.canvas.addEventListener('mousemove', (ev) => {
      if (!this.building) { this.renderer.ghost = null; return; }
      const rect = this.canvas.getBoundingClientRect();
      const { x, y } = this.renderer.tileAt(ev.clientX - rect.left, ev.clientY - rect.top);
      this.renderer.ghost = this.eraser ? null : { kind: this.buildKind, style: this.buildStyle, face: this.buildFace, x, y, ok: canPlace(this.map, this.buildKind, x, y) };
    });
    this.canvas.addEventListener('mouseleave', () => { this.renderer.ghost = null; });
    this.canvas.addEventListener('contextmenu', (ev) => {
      if (!this.building) {
        // v1.21.0: right-click (or long-press) an otter: ask them over, or message them
        const rect = this.canvas.getBoundingClientRect();
        const { wx, wy } = this.renderer.tileAt(ev.clientX - rect.left, ev.clientY - rect.top);
        const who = this.playerAt(wx, wy);
        if (!who) return;
        ev.preventDefault();
        this.openPlayerMenu(who, ev.clientX, ev.clientY);
        return;
      }
      ev.preventDefault();
      const rect = this.canvas.getBoundingClientRect();
      const { x, y } = this.renderer.tileAt(ev.clientX - rect.left, ev.clientY - rect.top);
      this.removeAt(x, y);
    });
    this.canvas.addEventListener('click', (ev) => {
      if (!this.meP) return;
      const rect = this.canvas.getBoundingClientRect();
      const { x: tx, y: ty } = this.renderer.tileAt(ev.clientX - rect.left, ev.clientY - rect.top);
      if (this.building) {
        if (this.eraser) { this.removeAt(tx, ty); return; }
        if (!canPlace(this.map, this.buildKind, tx, ty)) { toast("Can't build there."); return; }
        this.net.send({ t: 'place', kind: this.buildKind, style: this.buildStyle, x: tx, y: ty, face: this.buildFace });
        return;
      }
      const route = findPath(this.map, { x: this.meP.x, y: this.meP.y }, { x: tx, y: ty });
      if (!route) return;
      this.autoPath = route.steps; this.autoTarget = route.end;
      this.autoSit = this.map.seats.has(`${route.end.x},${route.end.y}`) && route.end.x === tx && route.end.y === ty;
      this.setPose('none');
      if (this.meP.sit) { this.meP.sit = false; this.net.send({ t: 'sit', on: false }); }
    });
    // v0.17.0: double-click a meeting-room banner to rename the room
    this.canvas.addEventListener('dblclick', (ev) => {
      const rect = this.canvas.getBoundingClientRect();
      const { wx, wy } = this.renderer.tileAt(ev.clientX - rect.left, ev.clientY - rect.top);
      const hit = this.renderer.roomBanners().find((b) => wx >= b.x - 6 && wx <= b.x + b.w + 6 && wy >= b.y - 4 && wy <= b.y + b.h + 4);
      if (!hit) return;
      ev.preventDefault();
      this.autoPath = []; this.autoTarget = null; this.autoSit = false;
      this.openRename(hit.zone);
    });
    const renameDlg = $('#rename') as HTMLDialogElement;
    ($('#rename-form') as HTMLFormElement).addEventListener('submit', (ev) => {
      ev.preventDefault();
      const zone = renameDlg.dataset.zone; const name = ($('#rename-input') as HTMLInputElement).value.trim();
      if (zone && name) this.net.send({ t: 'rename', zone, name });
      renameDlg.close();
    });
    $('#rename-cancel').addEventListener('click', () => renameDlg.close());
  }

  // ---------- v1.21.0: ask someone to come over ----------

  /** the other otter drawn at world pixel (wx, wy), nearest first */
  private playerAt(wx: number, wy: number): RenderPlayer | null {
    let best: RenderPlayer | null = null, bestD = Infinity;
    for (const p of this.players.values()) {
      if (p.id === this.me.id) continue;
      const dx = wx - (p.rx * TILE + 16), dy = wy - (p.ry * TILE + 4);
      if (Math.abs(dx) > 18 || dy < -40 || dy > 30) continue;
      const d = dx * dx + dy * dy;
      if (d < bestD) { best = p; bestD = d; }
    }
    return best;
  }

  private closePlayerMenu(): void { document.getElementById('pmenu')?.remove(); }

  private openPlayerMenu(p: RenderPlayer, cx: number, cy: number): void {
    this.closePlayerMenu();
    const menu = document.createElement('div');
    menu.id = 'pmenu'; menu.className = 'pmenu';
    const head = document.createElement('div'); head.className = 'pm-head'; head.textContent = p.name;
    const ask = document.createElement('button'); ask.dataset.act = 'ask'; ask.textContent = '👋 Ask to come over';
    ask.onclick = () => { this.closePlayerMenu(); this.askOver(p.id, p.name); };
    const msg = document.createElement('button'); msg.dataset.act = 'dm'; msg.textContent = '💬 Message';
    msg.onclick = async () => { this.closePlayerMenu(); await this.chat?.openDm([p.id]); this.setView('chat'); };
    menu.append(head, ask, msg);
    document.body.appendChild(menu);
    // keep it on screen
    const r = menu.getBoundingClientRect();
    menu.style.left = `${Math.max(8, Math.min(cx, innerWidth - r.width - 8))}px`;
    menu.style.top = `${Math.max(8, Math.min(cy, innerHeight - r.height - 8))}px`;
    const kinds = ['pointerdown', 'keydown', 'wheel', 'resize'] as const;
    const off = (ev: Event) => {
      if (ev.type === 'keydown' && (ev as KeyboardEvent).key !== 'Escape') return;
      if (ev.target instanceof Node && menu.contains(ev.target)) return;
      this.closePlayerMenu();
      for (const t of kinds) window.removeEventListener(t, off, true);
    };
    window.setTimeout(() => { for (const t of kinds) window.addEventListener(t, off, true); }, 0);
  }

  private askOver(id: string, name: string): void {
    this.net.send({ t: 'summon', to: id });
    toast(`Asked ${name} to come over…`);
  }

  /** someone asks me over: a card with Go / Not now (auto "no answer" after 30 s; Do not disturb answers busy) */
  private onSummon(from: string, name: string): void {
    const st = this.statuses.get(this.me.id);
    if (st && st.dndUntil > Date.now()) {
      this.net.send({ t: 'summon-reply', to: from, ok: false, reason: 'busy' });
      toast(`${name} asked you to come over (you're on Do not disturb)`, 6000);
      return;
    }
    this.closeSummon();
    const card = document.createElement('div');
    card.id = 'summon'; card.className = 'summon';
    const text = document.createElement('div'); text.className = 'sm-text';
    const b = document.createElement('b'); b.textContent = name; text.append(b, ' is asking you to come over');
    const go = document.createElement('button'); go.className = 'btn primary small-btn'; go.dataset.act = 'go'; go.textContent = 'Go to them';
    const no = document.createElement('button'); no.className = 'btn small-btn'; no.dataset.act = 'no'; no.textContent = 'Not now';
    const row = document.createElement('div'); row.className = 'sm-row'; row.append(no, go);
    card.append(text, row);
    $('#stage').appendChild(card);
    go.onclick = () => { this.closeSummon(); this.net.send({ t: 'summon-reply', to: from, ok: true }); this.walkToPlayer(from); };
    no.onclick = () => { this.closeSummon(); this.net.send({ t: 'summon-reply', to: from, ok: false }); };
    this.summonFrom = { id: from, timer: window.setTimeout(() => { this.closeSummon(); this.net.send({ t: 'summon-reply', to: from, ok: false, reason: 'timeout' }); }, 30_000) };
  }

  private closeSummon(): void {
    if (this.summonFrom) window.clearTimeout(this.summonFrom.timer);
    this.summonFrom = null;
    document.getElementById('summon')?.remove();
  }

  /** auto-walk to a free tile right next to someone (the nearest one by walking distance) */
  private walkToPlayer(id: string): void {
    const q = this.players.get(id), me = this.meP;
    if (!q || !me) { toast("They've left the office"); return; }
    const taken = new Set([...this.players.values()].filter((p) => p.id !== me.id).map((p) => `${p.x},${p.y}`));
    let best: { steps: PathStep[]; end: { x: number; y: number } } | null = null;
    for (const [dx, dy] of [[0, 1], [-1, 0], [1, 0], [0, -1], [-1, 1], [1, 1], [-1, -1], [1, -1]]) {
      const x = q.x + dx, y = q.y + dy;
      if (!isStandable(this.map, x, y) || this.map.seats.has(`${x},${y}`) || taken.has(`${x},${y}`)) continue;
      if (x === me.x && y === me.y) { best = { steps: [], end: { x, y } }; break; }
      const r = findPath(this.map, { x: me.x, y: me.y }, { x, y });
      if (r && r.end.x === x && r.end.y === y && (!best || r.steps.length < best.steps.length)) best = r;
    }
    best ??= findPath(this.map, { x: me.x, y: me.y }, { x: q.x, y: q.y });
    if (!best) { toast("Can't find a way over there"); return; }
    this.autoPath = best.steps; this.autoTarget = best.end; this.autoSit = false;
    this.setPose('none');
    if (me.sit) { me.sit = false; this.net.send({ t: 'sit', on: false }); }
  }

  // ---------- v0.18.0: build mode ----------

  private removeAt(x: number, y: number): void {
    const top = placementsAt(this.map, x, y)[0];
    if (top) this.net.send({ t: 'unplace', id: top.id });
    else toast('Nothing of yours to remove here (built-in furniture stays).');
  }

  private setBuilding(on: boolean): void {
    this.building = on;
    this.renderer.ghost = null;
    $('#btn-build').classList.toggle('on', on);
    show($('#build-panel'), on);
    if (on) { this.autoPath = []; this.autoTarget = null; this.renderBuildPanel(); }
  }

  private renderBuildPanel(): void {
    const tabs = $('#build-tabs'); tabs.innerHTML = '';
    for (const k of PLACE_KINDS) {
      const b = document.createElement('button'); b.className = 'ctl' + (k === this.buildKind && !this.eraser ? ' on' : ''); b.textContent = KIND_LABEL[k];
      b.onclick = () => { this.buildKind = k; this.eraser = false; this.renderBuildPanel(); };
      tabs.appendChild(b);
    }
    const erase = document.createElement('button'); erase.className = 'ctl' + (this.eraser ? ' on' : ''); erase.textContent = '🧹 Remove'; erase.title = 'Click something you placed to remove it (or right-click any time)';
    erase.onclick = () => { this.eraser = !this.eraser; this.renderBuildPanel(); };
    tabs.appendChild(erase);
    if (this.buildKind === 'chair') {
      const arrow: Record<Facing, string> = { down: '↓', up: '↑', left: '←', right: '→' };
      const face = document.createElement('button'); face.className = 'ctl'; face.textContent = `Faces ${arrow[this.buildFace]} (F)`;
      face.onclick = () => this.cycleFace();
      tabs.appendChild(face);
    }
    const grid = $('#build-grid'); grid.innerHTML = '';
    // v1.17.0: only styles that have a drawn piece are offered
    if (!furnitureHasArt(this.buildKind, this.buildStyle)) {
      for (let s = 0; s < STYLE_COUNT; s++) if (furnitureHasArt(this.buildKind, s)) { this.buildStyle = s; break; }
    }
    for (let s = 0; s < STYLE_COUNT; s++) {
      if (!furnitureHasArt(this.buildKind, s)) continue;
      const c = document.createElement('canvas'); c.width = 44; c.height = 44; c.className = 'thumb' + (s === this.buildStyle && !this.eraser ? ' sel' : '');
      c.title = `${KIND_LABEL[this.buildKind]} ${s + 1}`;
      drawFurnitureThumb(c, this.buildKind, s, this.buildKind === 'chair' ? this.buildFace : 'down');
      c.onclick = () => { this.buildStyle = s; this.eraser = false; this.renderBuildPanel(); };
      grid.appendChild(c);
    }
    $('#build-hint').textContent = this.eraser
      ? 'Click something you placed to remove it · Esc to leave build mode'
      : `${KIND_LABEL[this.buildKind]} ${this.buildStyle + 1} · click a tile to place · right-click to remove · ${this.buildKind === 'decor' ? 'decor goes anywhere' : this.buildKind === 'platform' ? 'rugs lie flat — walk right over them' : this.buildKind === 'block' ? 'obstacles block the way' : this.buildKind === 'chair' ? 'chairs are sit-able (F turns them)' : 'tables block the way'} · Esc to exit`;
  }

  private cycleFace(): void {
    const order: Facing[] = ['down', 'left', 'up', 'right'];
    this.buildFace = order[(order.indexOf(this.buildFace) + 1) % order.length];
    this.renderBuildPanel();
  }

  private setPose(pose: Pose): void {
    if (this.pose === pose) return;
    this.pose = pose;
    if (this.meP) this.meP.pose = pose;
    this.net.send({ t: 'pose', pose });
  }

  private tryMove(now: number): void {
    if (!this.meP) return;
    if (now - this.lastMoveAt < MOVE_MIN_INTERVAL_MS) return;
    const manual = this.held.size ? [...this.held].pop()! : null;
    const auto = manual ? null : this.autoPath.shift() ?? null;
    const dir = manual ?? auto?.dir ?? null;
    if (!dir) return;
    if (!manual && this.autoPath.length === 0) {
      // last step of a click-to-walk route: sit if the user clicked a seat
      window.setTimeout(() => {
        if (this.autoSit && this.meP && this.autoTarget && this.meP.x === this.autoTarget.x && this.meP.y === this.autoTarget.y && !this.meP.sit) { this.meP.sit = true; this.net.send({ t: 'sit', on: true }); }
        this.autoSit = false; this.autoTarget = null;
      }, 250);
    }
    this.lastMoveAt = now;
    this.meP.dir = dir; this.meP.face = dir;
    // Optimistic move using the same rule as the DO; its `pos` for us is authoritative.
    const next = tryStep(this.map, this.meP.x, this.meP.y, dir);
    if (next) { this.applyPrediction(next, dir); this.setPose('none'); }
    else if (!manual) { this.autoPath = []; this.autoTarget = null; this.autoSit = false; }
    // Only send moves that do something (or a facing change) — blocked spam would eat the server's budget.
    if (next || this.lastSentDir !== dir) { this.sendMove(dir, false, Boolean(next)); this.lastSentDir = dir; }
  }

  private applyPrediction(next: { x: number; y: number }, dir: Dir): void {
    if (!this.meP) return;
    this.meP.x = next.x; this.meP.y = next.y; this.meP.sit = false;
    this.meP.zone = zoneAt(this.map, next.x, next.y)?.name ?? null;
    this.meP.movedAt = performance.now();
    void dir;
  }

  /** v0.19.1: every move carries a seq; predicted steps wait in `pending` until the server acks them. */
  private sendMove(dir: Dir, jump: boolean, predicted: boolean): void {
    const seq = ++this.seqNo;
    this.net.send({ t: 'move', dir, seq, ...(jump ? { jump: true } : {}) });
    if (predicted) { if (!this.pending.length) this.pendingSince = performance.now(); this.pending.push({ seq, dir, jump }); }
  }

  private interact(): void {
    if (!this.meP) return;
    const key = `${this.meP.x},${this.meP.y}`;
    const z = this.map.meetTiles.get(key);
    if (z?.meetUrl) { window.open(z.meetUrl, '_blank', 'noopener'); return; }
    if (this.map.seats.has(key)) {
      this.meP.sit = !this.meP.sit;
      if (!this.meP.sit) this.noAutoSitKey = key; // stood up on purpose — don't sit back down until you move
      this.net.send({ t: 'sit', on: this.meP.sit });
    }
  }

  /** v0.19.0: a one-off toast when you stop somewhere interactive (replaces the old prompt pill). */
  private hintFor(key: string): void {
    const z = this.map.meetTiles.get(key);
    if (z?.meetUrl) { toast(`Ctrl to open ${this.roomName(z.name)} on Google Meet`); return; }
    const deskId = this.map.desks.get(key);
    if (deskId) {
      const owner = this.desks[deskId];
      if (owner?.userId === this.me.id) toast('Your desk · C to release it');
      else if (owner) toast(`${owner.name}'s desk`);
      else toast('Free desk · C to make it yours');
    }
  }

  private deskAction(): void {
    if (!this.meP) return;
    const id = this.map.desks.get(`${this.meP.x},${this.meP.y}`);
    if (!id) return;
    const owner = this.desks[id];
    if (owner?.userId === this.me.id) this.net.send({ t: 'desk', action: 'release' });
    else if (!owner) this.net.send({ t: 'desk', action: 'claim' });
    else toast(`That desk belongs to ${owner.name}.`);
  }

  private frame(t: number): void {
    const dt = Math.min(50, t - (this.prevT || t));
    this.prevT = t;
    this.renderer.step(this.players.values(), dt);
    // Safety net: if acks stop coming (dropped socket, reconnect), fall back to the last server position.
    // v1.7.1: pendingSince is reset on every ack — it used to mark when the queue first became non-empty, so on a
    // real round-trip (queue never empty while walking) this fired after 1.5s of walking and yanked you backwards.
    if (this.meP && this.serverPos && this.pending.length && performance.now() - this.pendingSince > 4000) { // v1.21.2: was 1.5 s -- a slow moment snapped you back, then forward again
      this.pending = []; this.resyncs++;
      this.meP.x = this.serverPos.x; this.meP.y = this.serverPos.y;
      this.meP.zone = zoneAt(this.map, this.meP.x, this.meP.y)?.name ?? null;
    }
    this.renderer.draw([...this.players.values()], this.meP, this.desks);
    if (this.chat?.miniNearby && t - this.nearbyHintAt > 500) { this.nearbyHintAt = t; this.chat.refreshNearby(); }
    // v0.19.0: standing still on a seat for 2s sits you down; stopping on a Meet tile / desk shows a one-off hint
    const now = performance.now();
    const still = this.meP && now - this.lastMoveAt > 350 && Math.abs(this.meP.rx - this.meP.x) < 0.01 && Math.abs(this.meP.ry - this.meP.y) < 0.01 && this.autoPath.length === 0;
    if (still && this.meP) {
      const key = `${this.meP.x},${this.meP.y}`;
      if (this.stillKey !== key) { this.stillKey = key; this.stillSince = now; if (this.noAutoSitKey !== key) this.noAutoSitKey = null; this.hintFor(key); }
      else if (!this.meP.sit && this.map.seats.has(key) && this.noAutoSitKey !== key && now - this.stillSince > 2000) { this.meP.sit = true; this.net.send({ t: 'sit', on: true }); }
    } else this.stillKey = null;
    requestAnimationFrame((tt) => this.frame(tt));
  }
  private prevT = 0;

  // ---------- chat / sidebar ----------

  /** v1.3.0: status pill in the top bar + the status / DND dialog */
  private renderStatusButton(): void {
    const st = this.statuses.get(this.me.id); const dnd = Boolean(st && st.dndUntil > Date.now());
    const b = $('#btn-status');
    b.textContent = st && (st.text || st.emoji) ? `${dnd ? '🔕 ' : ''}${st.emoji} ${st.text}`.trim() : dnd ? '🔕 Do not disturb' : '💬 Set a status';
    b.classList.toggle('on', dnd);
  }

  private openStatusDialog(): void {
    const st = this.statuses.get(this.me.id) ?? { text: '', emoji: '', dndUntil: 0 };
    const dlg = $('#status-dlg') as HTMLDialogElement;
    ($('#st-emoji') as HTMLInputElement).value = st.emoji; ($('#st-text') as HTMLInputElement).value = st.text;
    ($('#st-dnd') as HTMLSelectElement).value = st.dndUntil > Date.now() ? 'keep' : '0';
    ($('#st-dnd') as HTMLSelectElement).querySelector('option[value=keep]')!.textContent = st.dndUntil > Date.now() ? `Keep DND (until ${new Date(st.dndUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})` : 'Keep as is';
    for (const p of document.querySelectorAll<HTMLElement>('#status-dlg .preset')) p.onclick = () => { ($('#st-emoji') as HTMLInputElement).value = p.dataset.e ?? ''; ($('#st-text') as HTMLInputElement).value = p.dataset.t ?? ''; };
    dlg.showModal();
  }

  /** v1.1.0: Office ⇄ Chat views share the socket; the canvas just hides. */
  private setView(view: 'office' | 'chat'): void {
    $('#app').dataset.view = view;
    for (const tab of document.querySelectorAll<HTMLElement>('#topnav .tab')) tab.classList.toggle('on', tab.dataset.view === view);
    if (this.chat) { this.chat.visible = view === 'chat'; this.chat.renderSide(); this.chat.renderHead(); this.chat.renderMsgs(); this.chat.renderPanel(); if (view === 'chat' && this.chat.current) this.chat.renderMini(); }
    if (view === 'office') { this.renderer.resize(); } else { ($('#c-input') as HTMLTextAreaElement).focus(); }
    this.held.clear();
  }

  private async refreshDirectory(): Promise<void> {
    try {
      const r = await fetch('/api/users');
      if (r.ok) { this.directory = ((await r.json()) as { users: DirectoryUser[] }).users; for (const u of this.directory) if (u.status) this.statuses.set(u.id, u.status); this.renderUsers(); this.renderStatusButton(); }
    } catch { /* ignore */ }
  }

  private renderUsers(): void {
    const online = $('#online-list'); const all = $('#all-list');
    online.innerHTML = ''; all.innerHTML = '';
    const onlineIds = new Set(this.players.keys());
    for (const p of [...this.players.values()].sort((a, b) => a.name.localeCompare(b.name))) {
      online.appendChild(this.userRow(p.id, p.name, p.avatar, true, p.zone));
    }
    for (const u of this.directory) {
      if (onlineIds.has(u.id)) continue;
      all.appendChild(this.userRow(u.id, u.name, u.avatar, false, null));
    }
    $('#online-count').textContent = String(this.players.size);
  }

  private userRow(id: string, name: string, avatar: AvatarConfig, online: boolean, zone: string | null): HTMLElement {
    const row = document.createElement('div');
    row.className = 'user' + (online ? ' online' : '');
    const dot = document.createElement('span'); dot.className = 'avatar'; dot.style.background = avatar.color; dot.textContent = avatar.initials;
    const label = document.createElement('span'); label.className = 'uname';
    label.textContent = name + (id === this.me.id ? ' (you)' : '');
    const st = this.statuses.get(id); const stText = st && (st.text || st.emoji) ? `${st.dndUntil > Date.now() ? '🔕 ' : ''}${st.emoji} ${st.text}`.trim() : st && st.dndUntil > Date.now() ? '🔕 Do not disturb' : '';
    const sub = document.createElement('span'); sub.className = 'usub'; sub.textContent = stText || (zone ? `in ${this.roomName(zone)}` : online ? 'in the office' : 'offline');
    const text = document.createElement('div'); text.className = 'utext'; text.append(label, sub);
    row.append(dot, text);
    // v1.21.0: ask someone in the office to come over (the same as right-clicking their otter; works on phones too)
    if (online && id !== this.me.id) {
      const over = document.createElement('button');
      over.className = 'knock'; over.dataset.act = 'over'; over.textContent = '👋'; over.title = `Ask ${name} to come over`;
      over.onclick = (ev) => { ev.stopPropagation(); this.askOver(id, name); };
      row.appendChild(over);
    }
    if (this.features.slack && id !== this.me.id) {
      const knock = document.createElement('button');
      knock.className = 'knock'; knock.textContent = 'Knock'; knock.title = 'Ping them on Slack';
      knock.onclick = async () => {
        knock.disabled = true;
        const r = await fetch('/api/knock', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: id }) });
        toast(r.ok ? `Knocked — ${name} was pinged on Slack` : 'Knock failed');
        window.setTimeout(() => { knock.disabled = false; }, 5000);
      };
      row.appendChild(knock);
    }
    return row;
  }

  private bindUi(): void {
    $('#btn-mic').onclick = () => { this.mic = !this.mic; this.syncAvUi(); };
    for (const b of document.querySelectorAll<HTMLElement>('#emotes button')) {
      b.onclick = () => { this.playEmote(b.dataset.emote as EmoteKind); (document.activeElement as HTMLElement | null)?.blur?.(); };
    }
    $('#btn-cam').onclick = () => { this.cam = !this.cam; this.syncAvUi(); };
    const toggleScreen = async () => {
      if (!this.rtc) return;
      if (this.screen) { await this.rtc.stopScreen(); this.screen = false; }
      else { this.screen = await this.rtc.startScreen(); }
      this.syncAvUi();
    };
    $('#btn-screen').onclick = toggleScreen;
    $('#btn-build').onclick = () => { ($('#settings') as HTMLDialogElement).close(); this.setBuilding(true); };
    $('#screen-stop').onclick = toggleScreen;
    $('#audio-banner').onclick = () => {
      for (const a of this.audioEls.values()) a.play().catch(() => { /* ignore */ });
      show($('#audio-banner'), false);
    };
    $('#screen-close').onclick = () => { show($('#screen-overlay'), false); $('#stage').classList.remove('presenting'); };
    $('#btn-logout').onclick = () => { this.net.close(); this.rtc?.stop(); location.href = '/auth/logout'; };

    // settings
    const dlg = $('#settings') as HTMLDialogElement;
    let readSettings: (() => AvatarConfig) | null = null;
    $('#btn-settings').onclick = () => {
      readSettings = bindAvatarEditor('set', this.me);
      dlg.showModal();
    };
    ($('#settings-form') as HTMLFormElement).onsubmit = (ev) => {
      ev.preventDefault();
      const name = ($('#set-name') as HTMLInputElement).value.trim() || this.me.name;
      const avatar = readSettings ? readSettings() : this.me.avatar;
      this.me.name = name; this.me.avatar = avatar;
      if (this.meP) { this.meP.name = name; this.meP.avatar = avatar; }
      this.net.send({ t: 'profile', name, avatar });
      dlg.close();
    };
    $('#settings-cancel').onclick = () => dlg.close();
    const sdlg = $('#status-dlg') as HTMLDialogElement;
    ($('#status-form') as HTMLFormElement).onsubmit = async (ev) => {
      ev.preventDefault();
      const dndSel = ($('#st-dnd') as HTMLSelectElement).value;
      const body: Record<string, unknown> = { emoji: ($('#st-emoji') as HTMLInputElement).value, text: ($('#st-text') as HTMLInputElement).value };
      if (dndSel !== 'keep') body.dndMinutes = Number(dndSel);
      await fetch('/api/me/status', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      sdlg.close();
    };
    $('#status-clear').onclick = async () => { await fetch('/api/me/status', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emoji: '', text: '', dndMinutes: 0 }) }); sdlg.close(); };
    $('#status-cancel').onclick = () => sdlg.close();

    // v1.19.0: the workspace name for everyone; settings, members and personal invites for the owner and admins
    void fetch('/api/workspace').then((r) => (r.ok ? r.json() : null)).then((w: { name: string; role: string; domain: string } | null) => {
      if (!w) return;
      $('#ws-name').textContent = w.name;
      const admin = w.role === 'owner' || w.role === 'admin';
      show($('#admin'), admin);
      if (!admin) return;
      const ws = new WorkspaceSettings(w.domain, (m) => toast(m), (name) => { $('#ws-name').textContent = name; });
      $('#btn-ws').onclick = () => void ws.open('general');
      $('#btn-invite').onclick = () => void ws.open('invites');
    });
  }
}

// v1.8.8: build badge, bottom right of every screen — the version this page was served with; it turns red
// and clickable when the server reports a newer one (a cached page), so "is it updated?" is answered on sight
const PAGE_VERSION = (document.title.match(/\d+\.\d+\.\d+/) ?? [''])[0];
// v1.21.2: checked again on every reconnect (a deploy reconnects everyone) and every 5 minutes; the first time it turns
// stale a toast says so, since an open tab otherwise keeps running the old code until reloaded
let staleToldAt = '';
function checkVersion(): void {
  const v = $('#ver');
  fetch('/api/health', { cache: 'no-store' }).then((r) => r.json()).then((h: { version?: string }) => {
    if (h.version && h.version !== PAGE_VERSION) {
      v.textContent = `v${PAGE_VERSION} · v${h.version} is out — click to reload`; v.classList.add('stale'); v.onclick = () => location.reload();
      if (staleToldAt !== h.version) { staleToldAt = h.version; toast('Ottsy was updated: reload the page when you have a moment (bottom-right).', 12_000); }
    }
  }).catch(() => {});
}
$('#ver').textContent = `v${PAGE_VERSION}`;
checkVersion();
window.setInterval(checkVersion, 5 * 60_000);
void boot();
